import { type FC } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { JourneySummary } from '@/types';

import ClassPassengerIcon from '@/assets/icons/class-passenger.svg?react';
import CockpitIcon from '@/assets/icons/cockpit.svg?react';
import HazardIcon from '@/assets/icons/hazard.svg?react';
import IncidentIcon from '@/assets/icons/incident.svg?react';
import RouteIcon from '@/assets/icons/route-list.svg?react';
import TimeDeviation from '@/components/shared/time-deviation';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { TRAIN_STATUS_MAP } from '@/utils/constants';
import {
  SEARCH_PARAM_JOURNEY_ID,
  SEARCH_PARAM_ROUTE_LINE_NUMBER,
  SEARCH_PARAMS_ROUTE_ID,
} from '@/utils/constants/queries';
import { getJourneyDisplayName } from '@/utils/helpers/get-journey-display-name';

interface RouteContentProps {
  journey: JourneySummary;
}

const RouteContent: FC<RouteContentProps> = ({ journey }) => {
  const {
    journeyNumber,
    lineNumber,
    cockpitPassengers,
    totalPassengers,
    id: journeyId,
    deviation,
    hasIncident,
    journeyState,
    hasDangerousGoods,
  } = journey;
  const [, setSearchParams] = useSearchParams();

  const shouldShowIcons = !!(hasIncident || totalPassengers || cockpitPassengers);
  const journeyDisplayName = getJourneyDisplayName(journey);

  const icons = [
    { isAvailable: hasIncident, Icon: IncidentIcon },
    { isAvailable: !!totalPassengers, Icon: ClassPassengerIcon },
    { isAvailable: !!cockpitPassengers, Icon: CockpitIcon },
  ];

  const isJourneyCancelled = journeyState === TRAIN_STATUS_MAP.CANCELLED;
  const isJourneyCompleted = journeyState === TRAIN_STATUS_MAP.COMPLETED;

  const handleOnClick = () => {
    setSearchParams((prevParams) => {
      prevParams.set(SEARCH_PARAMS_ROUTE_ID, journeyId);
      prevParams.set(SEARCH_PARAM_JOURNEY_ID, journeyNumber);
      prevParams.set(SEARCH_PARAM_ROUTE_LINE_NUMBER, lineNumber);

      return prevParams;
    });
  };

  return (
    <div
      className="cursor-pointer flex justify-between items-center relative first:pt-0 py-2.5"
      onKeyDown={(event) => event.stopPropagation()}
      role="button"
      tabIndex={0}
      onClick={handleOnClick}
    >
      <div className="flex items-center gap-3">
        <div className="bg-secondary w-8 h-8 flex items-center justify-center rounded-lg">
          <RouteIcon className="size-5" />
        </div>
        <span
          className={cn('font-semibold text-sm', {
            'text-muted-foreground': isJourneyCompleted,
            'line-through decoration-2': isJourneyCancelled,
          })}
        >{`${journeyDisplayName} ${journeyNumber}`}</span>
      </div>
      <div className="flex items-center gap-3">
        {shouldShowIcons && (
          <div className="flex gap-2">
            {icons
              .filter(({ isAvailable }) => isAvailable)
              .map(({ Icon }) => (
                <Icon key={Icon.name} className="size-5 fill-muted-foreground" />
              ))}
          </div>
        )}
        {hasDangerousGoods && (
          <Badge
            variant="secondary"
            className={cn('rounded-full h-6 w-7.5 text-sm font-semibold px-1.5')}
          >
            <HazardIcon className="size-4.5!" />
          </Badge>
        )}
        <TimeDeviation time={deviation} />
      </div>
    </div>
  );
};

export default RouteContent;
